import { FC, useContext, useEffect, useMemo, useReducer } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { Grid } from '@mui/system';
import clsx from 'clsx';
import { isEmpty, isEqual, isNil } from 'lodash';
import { useLocation } from 'react-router-dom';
import { globalAction } from 'common/actions';
import { SYSTEM_COLLAPSE, SYSTEM_EXPAND, USER_EXPAND } from 'common/actions/row-groups/types';
import { ERROR_403 } from 'common/config/api';
import { firmSummaryUrl } from 'common/config/urls';
import { DEFAULT_CURRENCY } from 'common/constants/cap-table';
import {
  COMPANIES_FORBIDDEN_COMPANY_SUMMARY,
  COMPANIES_GET_COMPANY_SUMMARY_ACCESS,
  COMPANIES_NO_INVESTMENTS,
  COMPANIES_SUMMARY_TITLE,
  COMPANIES_TITLE,
} from 'common/constants/companies';
import { CAP_TABLE_CURRENCY_PAGE } from 'common/constants/currencyPageTypes';
import { FUND_INTELLIGENCE_ENABLER } from 'common/constants/features';
import { useCurrencySelector, useFormat } from 'common/hooks';
import { useStore } from 'common/store';
import { UseFormatValues } from 'common/types/format';
import { LayoutContextValues, UseStoreValues } from 'common/types/store';
import { CustomContainer, CustomScalarSpreadsheet, MessageBox } from 'components';
import CurrencyMenu from 'components/CurrencyMenu';
import { REGULAR_UNIT } from 'components/FeaturedSpreadsheet/constants';
import useCheckFeatures from 'components/FeatureEnabler/hooks';
import { GridSkeleton } from 'components/Grid';
import ScalarSpreadsheet from 'components/ScalarSpreadsheet';
import useWorkbook from 'components/ScalarSpreadsheet/utilities/useWorkbook';
import { LayoutContext } from 'context';
import { useTertiaryNavigationRegistration } from 'context/FirmBaseLayoutContext';
import type { TertiaryNavigationLinkItem } from 'context/FirmBaseLayoutContext';
import { NewCompanyDialog } from 'layouts/Main/components';
import { COMPANY_SUMMARY_SPREADSHEET_SLUG } from 'pages/Portfolio/common/constants/companySummary';
import { useCompanySummarySheet } from 'pages/Portfolio/hooks';
import { useGetAllocationStatusBreadcrumb } from 'services/hooks/allocation';
import { useGetCompanySummaryByMD } from 'services/hooks/companies';
import { getArrayValue, getBooleanValue, getObjectValue, getStringValue } from 'utilities';
import { COMPANY_SUMMARY_TEST_IDS } from './CompanySummary.testIds';
import { Charts, CompanyOverview, DailyMonitoring, Summary } from './components';
import { CompanySummaryProps } from './types';
import { rowsGroupsReducer } from '../../../../common/reducers/rowsGroups';
import { Row } from '../../../../common/types/scalarSpreadsheet';

const COMPANY_SUMMARY_CONTAINER_CLASS = `${COMPANY_SUMMARY_SPREADSHEET_SLUG}-container`;

const TAB_AT_A_GLANCE = 'at-a-glance';
const TAB_COMPANY_SUMMARY = 'company-summary';
const TAB_COMPANY_OVERVIEW = 'company-overview';
const TAB_DAILY_MONITORING = 'daily-monitoring';

const CompanySummary: FC<CompanySummaryProps> = props => {
  const { selectedMeasurementDate } = props;

  const [storeValue, dispatch] = useStore() as unknown as UseStoreValues;
  const { companyInfo, firmInfo, otherPermissions } = storeValue;

  const { setPageBreadcrumbs, showPageForm, togglePageForm, isExportMode } = useContext(
    LayoutContext
  ) as unknown as LayoutContextValues;

  const [, checkFeature] = useCheckFeatures();
  const location = useLocation();
  const isFundIntelligenceEnabled = checkFeature(FUND_INTELLIGENCE_ENABLER);

  const {
    data: companySummary,
    isFetching,
    isLoading,
  } = useGetCompanySummaryByMD({ company: companyInfo, measurementDate: selectedMeasurementDate });

  // If companySummary status is 403
  const forbiddenAccess = useMemo(() => isEqual(companySummary?.status?.toString(), ERROR_403), [companySummary]);

  const { allocationStatusBreadcrumb } = useGetAllocationStatusBreadcrumb();

  const companySummaryBreadcrumbs = useMemo(() => {
    if (firmInfo && companyInfo) {
      const { name, slug } = getObjectValue(firmInfo);
      const { name: companyName } = getObjectValue(companyInfo);

      return name && slug && companyName
        ? [
          {
            title: name,
            url: firmSummaryUrl(slug),
          },
          {
            title: COMPANIES_TITLE,
          },
          {
            title: companyName,
          },
          {
            title: COMPANIES_SUMMARY_TITLE,
          },
          allocationStatusBreadcrumb,
        ]
        : [];
    }

    return [];
  }, [companyInfo, firmInfo, allocationStatusBreadcrumb]);

  const firmSlug = getStringValue(firmInfo?.slug);
  const companySlug = getStringValue(companyInfo?.slug);

  const queryParams = selectedMeasurementDate?.slug ? `?date=${selectedMeasurementDate.slug}` : '';

  const { spreadsheet: companySummarySheet } = useCompanySummarySheet({
    companySlug,
    firmSlug,
    funds: companySummary?.funds,
    cflTotalsByFundNoSecurity: companySummary?.cfl_totals_by_fund_no_security,
    cflTotalsByFundSecurity: companySummary?.cfl_totals_by_fund_security,
    queryParams,
  });

  const [rowGroups, setRowGroups] = useReducer(rowsGroupsReducer, {});

  const parentRows = useMemo(
    () =>
      companySummarySheet?.rowConfig.filter((row: Row<object, object, object, object>) => row.hasSubRows) as Row<
        object,
        object,
        object,
        object
      >[],
    [companySummarySheet]
  );

  const hasExpandedRowGroup = useMemo(
    () => Object.values(rowGroups).some(rowGroup => [SYSTEM_EXPAND, USER_EXPAND].includes(rowGroup)),
    [rowGroups]
  );

  useEffect(() => {
    if (parentRows) {
      parentRows.forEach((row: Row<object, object, object, object>) => {
        if (row.alias && !rowGroups[row.alias as string]) {
          setRowGroups({ type: SYSTEM_COLLAPSE, row: row.alias });
        }
      });
    }
  }, [parentRows, rowGroups]);

  const spreadsheets = useMemo(
    () => (companySummarySheet?.yLength ? [companySummarySheet] : []),
    [companySummarySheet]
  );

  const { onChange, workbook } = useWorkbook(spreadsheets);

  const [format, formatDispatch] = useFormat({
    page: CAP_TABLE_CURRENCY_PAGE,
    sourceCurrency: companyInfo?.captable_currency ?? DEFAULT_CURRENCY,
    units: REGULAR_UNIT,
  }) as UseFormatValues;

  const {
    isCurrencyLoading,
    currencyMenuAnchorEl,
    handleCurrencyMenuOpen,
    handleCurrencyMenuClose,
    handleCurrencyChange,
    handleUnitClick,
    currencyDisplayLabel,
    currencyTooltipTitle,
    unitDisplayLabel,
    currencies,
    usedCurrencies,
    showMore,
    showLess,
  } = useCurrencySelector({
    format,
    dispatch: formatDispatch,
    fallbackMeasurementDateId: selectedMeasurementDate?.id,
    fallbackSourceCurrency: companyInfo?.captable_currency,
  });

  // Create companySummarySheet props
  const companySummarySheetProps = useMemo(() => {
    if (companySummarySheet) {
      return {
        ...companySummarySheet,
        data: getArrayValue(companySummarySheet?.data),
        format,
        formatDispatch,
        onChange,
        sheet: companySummarySheet,
        workbook,
      } as unknown as typeof ScalarSpreadsheet;
    }

    return null;
  }, [companySummarySheet, format, formatDispatch, onChange, workbook]);

  const tertiaryNavItems = useMemo<TertiaryNavigationLinkItem[]>(() => {
    const buildTabUrl = (tabId: string) => {
      const params = new URLSearchParams(location.search);
      params.set('tab', tabId);
      return `${location.pathname}?${params.toString()}`;
    };

    const dailyMonitoringItem = firmInfo?.daily_nav_enabled
      ? [{ value: TAB_DAILY_MONITORING, label: 'Daily Monitoring', to: buildTabUrl(TAB_DAILY_MONITORING) }]
      : [];

    if (isFundIntelligenceEnabled) {
      return [
        { value: TAB_AT_A_GLANCE, label: 'At a Glance', to: buildTabUrl(TAB_AT_A_GLANCE) },
        { value: TAB_COMPANY_SUMMARY, label: 'Company Summary', to: buildTabUrl(TAB_COMPANY_SUMMARY) },
        { value: TAB_COMPANY_OVERVIEW, label: 'Company Overview', to: buildTabUrl(TAB_COMPANY_OVERVIEW) },
        ...dailyMonitoringItem,
      ];
    }
    return [
      { value: TAB_COMPANY_SUMMARY, label: 'Company Summary', to: buildTabUrl(TAB_COMPANY_SUMMARY) },
      { value: TAB_COMPANY_OVERVIEW, label: 'Company Overview', to: buildTabUrl(TAB_COMPANY_OVERVIEW) },
      ...dailyMonitoringItem,
    ];
  }, [isFundIntelligenceEnabled, firmInfo?.daily_nav_enabled, location.pathname, location.search]);

  const tertiaryNavigationState = useMemo(
    () => ({
      ariaLabel: 'Company summary views',
      items: tertiaryNavItems,
      actions: [
        {
          type: 'currencySelector' as const,
          currency: isCurrencyLoading
            ? <CircularProgress size={14} color="inherit" />
            : currencyDisplayLabel,
          currencyTooltipTitle: isCurrencyLoading ? undefined : currencyTooltipTitle,
          unit: unitDisplayLabel,
          onCurrencyClick: handleCurrencyMenuOpen,
          onUnitClick: handleUnitClick,
          disabled: isCurrencyLoading,
          isVisible: !isExportMode,
        },
      ],
    }),
    [
      tertiaryNavItems,
      isCurrencyLoading,
      currencyDisplayLabel,
      currencyTooltipTitle,
      unitDisplayLabel,
      handleCurrencyMenuOpen,
      handleUnitClick,
      isExportMode,
    ]
  );

  useTertiaryNavigationRegistration(tertiaryNavigationState);

  const activeTab = useMemo(() => {
    const tabParam = new URLSearchParams(location.search).get('tab');
    if (tabParam === TAB_DAILY_MONITORING && firmInfo?.daily_nav_enabled) {
      return TAB_DAILY_MONITORING;
    }
    if (isFundIntelligenceEnabled) {
      if (tabParam === TAB_COMPANY_SUMMARY) {
        return TAB_COMPANY_SUMMARY;
      }
      if (tabParam === TAB_COMPANY_OVERVIEW) {
        return TAB_COMPANY_OVERVIEW;
      }
      return TAB_AT_A_GLANCE;
    }
    return tabParam === TAB_COMPANY_OVERVIEW ? TAB_COMPANY_OVERVIEW : TAB_COMPANY_SUMMARY;
  }, [isFundIntelligenceEnabled, firmInfo?.daily_nav_enabled, location.search]);

  // Set Company Summary page breadcrumbs
  useEffect(() => {
    setPageBreadcrumbs?.(companySummaryBreadcrumbs);

    return () => setPageBreadcrumbs?.(null);
  }, [companySummaryBreadcrumbs, setPageBreadcrumbs]);

  // Show Loading Progress
  useEffect(() => {
    dispatch?.(globalAction.showLoadingProgress(isLoading));
  }, [dispatch, isLoading]);

  // Loading Grid Skeleton while fetching data
  if (isLoading || isNil(otherPermissions)) {
    return (
      <Grid>
        <GridSkeleton maxColumns={11} maxRows={4} />
      </Grid>
    );
  }

  // Forbidden access to the Company Summary page
  if (forbiddenAccess) {
    return (
      <Box data-testid={COMPANY_SUMMARY_TEST_IDS.forbiddenState}>
        <MessageBox
          fullWidth={false}
          tagline={COMPANIES_GET_COMPANY_SUMMARY_ACCESS}
          title={COMPANIES_FORBIDDEN_COMPANY_SUMMARY}
        />
      </Box>
    );
  }

  // No Investments on this Company (investment tabs only; Overview and Daily Monitoring do not need funds)
  if (
    companySummary
    && isEmpty(companySummary.funds)
    && activeTab !== TAB_COMPANY_OVERVIEW
    && activeTab !== TAB_DAILY_MONITORING
  ) {
    return (
      <Box data-testid={COMPANY_SUMMARY_TEST_IDS.noInvestmentsState}>
        <MessageBox title={COMPANIES_NO_INVESTMENTS} fullWidth={false} />
      </Box>
    );
  }

  if (isFundIntelligenceEnabled) {
    return (
      <>
        <CurrencyMenu
          anchorEl={currencyMenuAnchorEl}
          data-testid="company-summary-currency-menu"
          currencies={currencies}
          isCurrencyLoading={isCurrencyLoading}
          onClose={handleCurrencyMenuClose}
          onCurrencyChange={handleCurrencyChange}
          showMore={showMore}
          showLess={showLess}
          usedCurrencies={usedCurrencies}
        />
        {activeTab === TAB_AT_A_GLANCE && (
          <Box data-testid={COMPANY_SUMMARY_TEST_IDS.atAGlanceTabPanel}>
            <Charts selectedMeasurementDate={selectedMeasurementDate} />
          </Box>
        )}
        {activeTab === TAB_COMPANY_SUMMARY && (
          <Box data-testid={COMPANY_SUMMARY_TEST_IDS.companySummaryTabPanel}>
            <Summary
              companySummarySheetProps={companySummarySheetProps}
              companySummary={companySummary}
              isFetching={isFetching}
              rowGroups={rowGroups}
              setRowGroups={setRowGroups}
              containerClassName={COMPANY_SUMMARY_CONTAINER_CLASS}
              hasExpandedRowGroup={hasExpandedRowGroup}
            />
          </Box>
        )}
        {activeTab === TAB_COMPANY_OVERVIEW && (
          <Box pt={5} data-testid={COMPANY_SUMMARY_TEST_IDS.companyOverviewTabPanel}>
            <CompanyOverview measurementDate={selectedMeasurementDate?.date} />
          </Box>
        )}
        {activeTab === TAB_DAILY_MONITORING && (
          <Box pt={5} data-testid={COMPANY_SUMMARY_TEST_IDS.dailyMonitoringTabPanel}>
            <DailyMonitoring selectedMeasurementDate={selectedMeasurementDate} />
          </Box>
        )}
        <NewCompanyDialog open={getBooleanValue(showPageForm)} onClose={togglePageForm || (() => {})} editMode />
      </>
    );
  }

  return (
    <>
      <CurrencyMenu
        anchorEl={currencyMenuAnchorEl}
        data-testid="company-summary-currency-menu"
        currencies={currencies}
        isCurrencyLoading={isCurrencyLoading}
        onClose={handleCurrencyMenuClose}
        onCurrencyChange={handleCurrencyChange}
        showMore={showMore}
        showLess={showLess}
        usedCurrencies={usedCurrencies}
      />
      {activeTab === TAB_COMPANY_SUMMARY && (
        <Box pt={5} data-testid={COMPANY_SUMMARY_TEST_IDS.companySummaryTabPanel}>
          <CustomContainer className={clsx(COMPANY_SUMMARY_CONTAINER_CLASS, { expanded: hasExpandedRowGroup })}>
            {companySummarySheetProps && companySummary && !isEmpty(companySummary) && (
              <Grid size={{ xs: 12 }}>
                <CustomScalarSpreadsheet
                  {...companySummarySheetProps}
                  isLoading={isFetching}
                  rowGroups={rowGroups}
                  setRowGroups={setRowGroups}
                />
              </Grid>
            )}
          </CustomContainer>
        </Box>
      )}
      {activeTab === TAB_COMPANY_OVERVIEW && (
        <Box pt={5} data-testid={COMPANY_SUMMARY_TEST_IDS.companyOverviewTabPanel}>
          <CompanyOverview measurementDate={selectedMeasurementDate?.date} />
        </Box>
      )}
      {activeTab === TAB_DAILY_MONITORING && (
        <Box pt={5} data-testid={COMPANY_SUMMARY_TEST_IDS.dailyMonitoringTabPanel}>
          <DailyMonitoring selectedMeasurementDate={selectedMeasurementDate} />
        </Box>
      )}
      <NewCompanyDialog open={getBooleanValue(showPageForm)} onClose={togglePageForm || (() => {})} editMode />
    </>
  );
};

export default CompanySummary;
